import type {
  MindsetLicence,
  MindsetRunToken,
  MindsetSessionConfig,
  MindsetStopPayload,
  MindsetVitalClient,
  MindsetVitalClientOptions,
} from './mindset-types'
import { attachCameraToVideo, PATIENT_CAMERA } from './patient-camera'

/** Where the vendor bundle is served from. Static assets, untouched by Vite's bundler. */
const SDK_MODULE_URL = '/sdk/mindset/index.js'
const WORKER_DIRECTORY = '/sdk/mindset'

/**
 * Whether this build carries the camera-replacement shim - the same define the FaceHeart client
 * reads, replaced with a literal at build time so Rollup removes the branch and everything only it
 * reaches from the production bundle (vite.config.ts says why at length).
 */
declare const __FH_CAMERA_REPLACEMENT__: boolean

declare global {
  interface Window {
    __mindsetCreateVitalClient?: (options: MindsetVitalClientOptions) => VendorClient
  }
}

/** The vendor's own client, as `createVitalClient` returns it. */
interface VendorClient {
  on(event: string, listener: (payload: unknown) => void): void
  init(): Promise<void>
  startPreviewMode(video: HTMLVideoElement): Promise<void>
  authorize(): Promise<void>
  start(config: MindsetSessionConfig, video: HTMLVideoElement): Promise<void>
  stop(): Promise<MindsetStopPayload>
  destroy?: () => void
  getVersion?: () => Promise<string>
}

let moduleLoad: Promise<(options: MindsetVitalClientOptions) => VendorClient> | null = null

/**
 * Loads the vendor bundle, which is an ES module rather than a UMD build with a global.
 *
 * A DYNAMIC IMPORT, and the shape it replaced is worth naming because it shipped. This function used
 * to build an inline `<script type="module">` that imported the bundle and hung `createVitalClient`
 * on `window`, under a comment claiming `script-src 'self'` admitted it "because it is same-origin".
 * That is wrong about what `'self'` means: it governs the URLs an EXTERNAL script may be fetched
 * from and says nothing about inline execution, which needs `'unsafe-inline'`, a hash or a nonce
 * whatever the origin. The deployed page refused it - "Executing inline script violates the
 * following Content Security Policy directive" - and the scan died before the camera was ever asked
 * for.
 *
 * `import()` of a same-origin URL is a script FETCH and is exactly what `script-src 'self'` is for,
 * so nothing about the policy needs to change to admit it. `@vite-ignore` keeps the bundler's hands
 * off a specifier it would otherwise try to resolve and bundle at build time; the vendor bundle is a
 * static asset served from public/, not a module in this app's graph.
 *
 * The `window.__mindsetCreateVitalClient` check stays: it is the seam a Playwright journey injects
 * through, and it is also the warm path on a second scan in one page.
 */
function loadVendorFactory(): Promise<(options: MindsetVitalClientOptions) => VendorClient> {
  if (window.__mindsetCreateVitalClient) {
    return Promise.resolve(window.__mindsetCreateVitalClient)
  }

  moduleLoad ??= import(/* @vite-ignore */ SDK_MODULE_URL).then((module) => {
    const factory = (module as { createVitalClient?: (options: MindsetVitalClientOptions) => VendorClient })
      .createVitalClient

    if (!factory) {
      throw new Error(`${SDK_MODULE_URL} loaded without exposing createVitalClient`)
    }

    return factory
  })

  return moduleLoad
}

/**
 * The real adapter. Everything with a decision in it is in mindset-session.ts, behind
 * `MindsetVitalClient`; this file is the part that cannot be unit-tested because it needs a camera,
 * a Web Worker and a WASM module, and it is kept as thin as that division allows.
 *
 * **The camera is this app's to open, not the vendor's.** The Mindset bundle contains no
 * `getUserMedia` call anywhere: it reads frames off an `HTMLVideoElement` through
 * `requestVideoFrameCallback` and expects something else to have put a stream on it. That is the
 * opposite of FaceHeart, whose SDK opens the camera itself and needed a `document.createElement`
 * patch to stop it tripping over autoplay policy.
 */
export function createBrowserMindsetClient(options: {
  readonly authenticator: (runToken: MindsetRunToken) => Promise<MindsetLicence>
  /**
   * A camera the CALLER opened, inside the patient's tap.
   *
   * Not this file's job any more, and that is the whole point: iOS refuses `getUserMedia` once the
   * activation from the tap has expired, and `init()` above spends seconds loading a vendor bundle
   * before this client would have asked. See src/sdk/patient-camera.ts and docs/decisions.md D-24.
   * Null is the journey's recorded-footage path, where there is no camera to hand over.
   */
  readonly stream?: MediaStream | null
}): MindsetVitalClient {
  let vendor: VendorClient | null = null
  let stream: MediaStream | null = options.stream ?? null
  const pending: { event: string; listener: (payload: unknown) => void }[] = []

  async function vendorClient(): Promise<VendorClient> {
    if (vendor) {
      return vendor
    }
    const factory = await loadVendorFactory()
    vendor = factory({
      workerDirectory: WORKER_DIRECTORY,
      authenticator: options.authenticator,
    })
    for (const subscription of pending) {
      vendor.on(subscription.event, subscription.listener)
    }
    pending.length = 0
    return vendor
  }

  return {
    on(event, listener) {
      // Subscriptions are taken before init() by the session runner - `ready` is emitted during it -
      // so they are held until there is a vendor client to attach them to.
      const subscription = { event, listener: listener as (payload: unknown) => void }
      if (vendor) {
        vendor.on(subscription.event, subscription.listener)
      } else {
        pending.push(subscription)
      }
    },

    async init() {
      const client = await vendorClient()
      await client.init()
    },

    async startPreviewMode(video) {
      await attachCamera(video)
      const client = await vendorClient()
      await client.startPreviewMode(video)
    },

    async authorize() {
      const client = await vendorClient()
      await client.authorize()
    },

    async start(config, video) {
      const client = await vendorClient()
      await client.start(config, video)
    },

    async stop() {
      const client = await vendorClient()
      return client.stop()
    },

    destroy() {
      stream?.getTracks().forEach((track) => track.stop())
      stream = null
      vendor?.destroy?.()
      vendor = null
    },
  }

  async function attachCamera(video: HTMLVideoElement): Promise<void> {
    if (__FH_CAMERA_REPLACEMENT__ && window.__VIDEO_URL) {
      // The journey affordance, exactly as the FaceHeart client carries it and compiled out of the
      // production bundle by the same define: recorded footage in place of a camera, so a Playwright
      // run can drive the real SDK against a scan whose answer is known.
      video.muted = true
      video.setAttribute('playsinline', '')
      video.src = window.__VIDEO_URL
      video.loop = true
      await video.play()
      return
    }

    if (!stream) {
      // No pre-opened stream: the caller is not the scan flow. Kept as a fallback rather than a throw
      // so this client stays usable on its own, but the flow always hands one over - on iOS a
      // getUserMedia reached from here is a silent refusal (docs/decisions.md D-24).
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('This browser exposes no camera API')
      }

      stream = await navigator.mediaDevices.getUserMedia(PATIENT_CAMERA)
    }

    // Usually a no-op: the flow attached this stream when the scan screen opened, which is what keeps
    // an iOS preview from going black while this client's 55 MB of bundle loads (D-25).
    await attachCameraToVideo(video, stream)
  }

}
